import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Settings = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "general-settings",
        "brand-identity",
        "plan-management",
        "billing",
        "users-and-permissions",
        "payments",
        "notifications",
        "checkout",
        "shipping-and-delivery",
        "domains",
        "tracking-events",
        "policies",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Settings Overview | xPage Help Center"
        description="Learn how to configure your xPage store settings, including general settings, branding, plan management, billing, payments, permissions, notifications, checkout, shipping, domains, tracking events, and policies."
        keywords="xPage settings, store settings, general settings, brand identity, billing, plan management, users permissions, payments setup, checkout settings, shipping settings, domain management, tracking events, store policies"
        canonical="https://help.xpage.ai/help/settings/settings"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Xpage Settings Overview
          </h1>
          <p className="text-sm text-systemLabel">
            This chapter covers Xpage's general settings, brand identity, plan
            management, billing, user permissions, payments, notifications,
            checkout, shipping, domains, tracking events, and policy management.
            It provides a clear roadmap for configuring your store efficiently.
          </p>
        </header>

        {/* General Settings Section */}
        <section id="general-settings" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            General Settings
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Manage your store's basic information in Settings → General:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Store name, contact email, phone number</li>
              <li>Default language and time zone</li>
              <li>Currency settings</li>
            </ul>
          </div>
        </section>

        {/* Brand Identity Section */}
        <section id="brand-identity" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Brand Identity
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Define your store's visual presence in Settings → Brand Identity:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Upload logos and favicons</li>
              <li>Set primary and secondary brand colors</li>
              <li>
                Configure fonts and style elements for landing pages and product
                displays
              </li>
            </ul>
          </div>
        </section>

        {/* Plan Management Section */}
        <section id="plan-management" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Plan Management
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              View and manage your subscription in Settings → Plan:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Check your current Xpage plan and its features</li>
              <li>
                Upgrade or modify your plan to unlock additional capabilities
              </li>
            </ul>
          </div>
        </section>

        {/* Billing Section */}
        <section id="billing" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Billing
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Manage all financial aspects in Settings → Billing:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Add or update payment methods</li>
              <li>View past invoices and download receipts</li>
              <li>Track subscription charges and billing history</li>
            </ul>
          </div>
        </section>

        {/* Users and Permissions Section */}
        <section id="users-and-permissions" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Users and Permissions
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Control team access in Settings → Users and Permissions:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Add new users and assign roles</li>
              <li>Set permissions for different sections of the store</li>
              <li>Ensure secure access for team members</li>
            </ul>
          </div>
        </section>

        {/* Payments Section */}
        <section id="payments" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Payments
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Configure payment options in Settings → Payments:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Add and manage payment gateways</li>
              <li>Enable multiple payment methods for checkout</li>
              <li>Test transactions to ensure seamless payment processing</li>
            </ul>
          </div>
        </section>

        {/* Notifications Section */}
        <section id="notifications" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Notifications
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Manage store alerts in Settings → Notifications:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Enable or disable email and backend notifications</li>
              <li>Verify sender email addresses</li>
              <li>
                Customize notification templates for orders, payments, and
                shipping updates
              </li>
            </ul>
          </div>
        </section>

        {/* Checkout Section */}
        <section id="checkout" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Checkout
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Customize the checkout experience in Settings → Checkout:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Configure required fields for customers</li>
              <li>Enable guest checkout</li>
              <li>Optimize checkout options to improve conversion</li>
            </ul>
          </div>
        </section>

        {/* Shipping and Delivery Section */}
        <section id="shipping-and-delivery" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Shipping and Delivery
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Set shipping rules in Settings → Shipping and Delivery:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Configure shipping zones, carriers, and rates</li>
              <li>Set free shipping or local delivery options</li>
              <li>Define pickup or same-day delivery rules</li>
            </ul>
          </div>
        </section>

        {/* Domains Section */}
        <section id="domains" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Domains
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Manage your store's web addresses in Settings → Domains:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Connect custom domains or subdomains</li>
              <li>Configure SSL and URL redirects</li>
              <li>Ensure your store is accessible professionally</li>
            </ul>
          </div>
        </section>

        {/* Tracking Events Section */}
        <section id="tracking-events" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Tracking Events
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Monitor activity in Settings → Tracking Events:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Set up event tracking for sales and user interactions</li>
              <li>
                Integrate with analytics tools to measure marketing performance
              </li>
              <li>Optimize campaigns based on visitor and conversion data</li>
            </ul>
          </div>
        </section>

        {/* Policies Section */}
        <section id="policies" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Policies
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Create and manage store policies in Settings → Policies:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Return, privacy, and terms of service policies</li>
              <li>Use templates or customize content</li>
              <li>Policy links appear automatically in the checkout footer</li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                Xpage Settings centralizes all store configuration in one place,
                giving you full control over operations, branding, payments,
                shipping, and customer experience.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#general-settings"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "general-settings"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                General Settings
              </a>
            </li>
            <li>
              <a
                href="#brand-identity"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "brand-identity"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Brand Identity
              </a>
            </li>
            <li>
              <a
                href="#plan-management"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "plan-management"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Plan Management
              </a>
            </li>
            <li>
              <a
                href="#billing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "billing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Billing
              </a>
            </li>
            <li>
              <a
                href="#users-and-permissions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "users-and-permissions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Users & Permissions
              </a>
            </li>
            <li>
              <a
                href="#payments"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payments"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payments
              </a>
            </li>
            <li>
              <a
                href="#notifications"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "notifications"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Notifications
              </a>
            </li>
            <li>
              <a
                href="#checkout"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "checkout"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Checkout
              </a>
            </li>
            <li>
              <a
                href="#shipping-and-delivery"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "shipping-and-delivery"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Shipping & Delivery
              </a>
            </li>
            <li>
              <a
                href="#domains"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "domains"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Domains
              </a>
            </li>
            <li>
              <a
                href="#tracking-events"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "tracking-events"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Tracking Events
              </a>
            </li>
            <li>
              <a
                href="#policies"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "policies"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Policies
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Settings;
